import { useState, useEffect, useMemo, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { fetchSubmissions, removeSubmission } from '../reducers/submissionsSlice';
import { openConfirmationModal } from '../reducers/uiSlice';
import { confirmationService } from '../services/confirmationService';
import { db } from '../lib/database';
import { toastService } from '../services/toastService';
import { submissionService } from '../services/submissionService';

export const useFormData = (filter) => {
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();
  const formId = searchParams.get('formId');
  const [formTitle, setFormTitle] = useState('Form Data');
  const [hasOrphanedSubmissions, setHasOrphanedSubmissions] = useState(false);
  
  const { items: submissions, status } = useSelector(state => state.submissions);
  const { forms } = useSelector(state => state.forms);

  const filteredSubmissions = useMemo(() => {
    let submissionsToFilter = submissions.filter(s => s.syncStatus !== 'deleted');

    if (formId) {
      submissionsToFilter = submissionsToFilter.filter(s => String(s.form_id) === String(formId));
    }
    
    if (filter === 'pending') {
      submissionsToFilter = submissionsToFilter.filter(s => s.syncStatus === 'pending');
    }

    const processedSubmissions = submissionsToFilter.map(sub => {
      const parentForm = forms.find(f => String(f.id) === String(sub.form_id));
      const isOrphan = !parentForm;
      const displayFormName = parentForm?.title || 
                              parentForm?.name || 
                              sub.form?.name || 
                              sub.formName || 
                              'Unknown Form';

      return {
        ...sub,
        isOrphan,
        formName: displayFormName
      };
    });

    const hasOrphans = processedSubmissions.some(s => s.isOrphan);
    setTimeout(() => setHasOrphanedSubmissions(hasOrphans), 0);

    return processedSubmissions.sort((a, b) => new Date(b.created_at || b.createdAt) - new Date(a.created_at || a.createdAt));
  }, [submissions, formId, filter, forms]);

  useEffect(() => {
    dispatch(fetchSubmissions({ page: 1, limit: 1000, forceRefresh: true }));
    if (formId) {
      db.forms.get(formId).then(form => {
        if (form) setFormTitle(form.title || form.name);
      });
    } else {
      setFormTitle('All Submissions');
    }
  }, [formId, dispatch]);

  const handleDelete = useCallback(id => {
    const confirmId = `delete-${id}`;
    confirmationService.register(confirmId, () => {
      const deletePromise = submissionService.deleteSubmission(id);
      toastService.notifyPromise(deletePromise, {
          pending: 'Deleting submission...',
          success: 'Submission deleted!'
      });
      deletePromise.then(() => dispatch(removeSubmission(id)));
    });

    dispatch(
      openConfirmationModal({
        title: 'Delete Submission',
        message: 'Are you sure you want to delete this submission? This action cannot be undone.',
        type: 'destructive',
        confirmId
      })
    );
  }, [dispatch]);

  return {
    formId,
    formTitle,
    submissions: filteredSubmissions,
    status,
    handleDelete,
    hasOrphanedSubmissions
  };
};

export default useFormData;